import {
  IPolicyBuilderConfig,
  IPolicyBuilderValidation,
  IPolicyConditionField,
  IPolicyFormField,
  IPolicyOperator,
  IPolicyOptionSource,
  IPolicyRuleCategory,
  IPolicySubSection,
} from "../types/builder";

export interface PolicyBuilderIndexes {
  config: IPolicyBuilderConfig;
  fieldsByKey: Record<string, IPolicyFormField>;
  subSectionsByKey: Record<string, IPolicySubSection>;
  optionSourcesByKey: Record<string, IPolicyOptionSource>;
  validationsByKey: Record<string, IPolicyBuilderValidation>;
  ruleCategoriesByKey: Record<string, IPolicyRuleCategory>;
  conditionFieldsByKey: Record<string, IPolicyConditionField>;
  operatorsBySymbol: Record<string, IPolicyOperator>;
}

function indexByKey<T extends { key: string }>(items: T[]): Record<string, T> {
  return items.reduce<Record<string, T>>((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {});
}

export function buildPolicyBuilderIndexes(
  config: IPolicyBuilderConfig,
): PolicyBuilderIndexes {
  return {
    config,
    fieldsByKey: indexByKey(config.form_fields),
    subSectionsByKey: indexByKey(config.sub_sections),
    optionSourcesByKey: indexByKey(config.option_sources),
    validationsByKey: indexByKey(config.validations),
    ruleCategoriesByKey: indexByKey(config.rule_categories),
    conditionFieldsByKey: indexByKey(config.condition_field_library),
    operatorsBySymbol: config.operator_library.reduce<
      Record<string, IPolicyOperator>
    >((acc, operator) => {
      acc[operator.symbol] = operator;
      return acc;
    }, {}),
  };
}

export function getValidationsByKeys(
  indexes: PolicyBuilderIndexes,
  keys: string[] | null,
): IPolicyBuilderValidation[] {
  if (!keys?.length) {
    return [];
  }

  return keys
    .map((key) => indexes.validationsByKey[key])
    .filter((validation): validation is IPolicyBuilderValidation =>
      Boolean(validation),
    );
}

export function getOperatorsBySymbols(
  indexes: PolicyBuilderIndexes,
  symbols: string[],
): IPolicyOperator[] {
  return symbols
    .map((symbol) => indexes.operatorsBySymbol[symbol])
    .filter((operator): operator is IPolicyOperator => Boolean(operator));
}

export const STRUCTURAL_SUB_SECTION_KEYS = new Set([
  "RULE_CARD",
  "RULE_DEFINITION",
  "RULE_GROUPS",
  "CONDITION_LINES",
]);

export function isStructuralSubSection(subSectionKey: string): boolean {
  return STRUCTURAL_SUB_SECTION_KEYS.has(subSectionKey);
}

export function getRuleGroupSubSectionsForCategory(
  category: IPolicyRuleCategory,
  indexes: PolicyBuilderIndexes,
): IPolicySubSection[] {
  return category.sub_section_keys
    .map((subSectionKey) => indexes.subSectionsByKey[subSectionKey])
    .filter((subSection): subSection is IPolicySubSection =>
      Boolean(subSection),
    )
    .filter((subSection) => !isStructuralSubSection(subSection.key));
}
